/**
 * The native apps open /#/app-redirect?token=... to hand a signed-in user over
 * to the PWA. That token is a login credential, and while it sits in the
 * address bar every recorder that reads the URL keeps a copy: PostHog session
 * replay, the `raw_url`/`resolved_url` on our own $pageview, Sentry's
 * navigation transactions, Clarity.
 *
 * stripAppRedirectToken() runs first thing in main.ts, before any of those
 * start, takes the token out of the URL with history.replaceState and keeps it
 * in memory only. AppRedirectComponent then collects it with
 * takeAppRedirectToken(). Nothing is written to storage.
 */
let pendingToken: string | null = null;

export function stripAppRedirectToken(): void {
  const hash = window.location.hash;
  const queryStart = hash.indexOf('?');
  if (queryStart === -1) return;

  const path = hash.slice(1, queryStart);
  if (!/^\/?app-redirect\/?$/.test(path)) return;

  const params = new URLSearchParams(hash.slice(queryStart + 1));
  const token = params.get('token');
  if (!token) return;

  pendingToken = token;
  params.delete('token');
  const rest = params.toString();
  try {
    history.replaceState(
      history.state,
      '',
      window.location.pathname + window.location.search + '#' + path + (rest ? '?' + rest : '')
    );
  } catch {
    // Some in-app browsers restrict the History API. The component still gets
    // the token from memory; only the masking is lost.
  }
}

/** Hands the stripped token over once, then forgets it. */
export function takeAppRedirectToken(): string | null {
  const token = pendingToken;
  pendingToken = null;
  return token;
}
